import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Play, Square, Footprints, Activity, MapPin, Flame, Timer, Gauge, Trash2, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import client, { formatErr } from "../lib/api";
import RunMap from "../components/RunMap";
import RunSummary from "../components/RunSummary";

// Haversine — meters between two lat/lng points.
function haversine(a, b) {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
}

function fmtTime(s) {
    s = Math.max(0, Math.floor(s));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
        ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
        : `${m}:${String(sec).padStart(2, "0")}`;
}

function fmtKm(m) {
    return (m / 1000).toFixed(2);
}

export default function RunTracker() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const parleyId = searchParams.get("parley") || null;

    const [activity, setActivity] = useState("run");
    const [status, setStatus] = useState("idle"); // idle | running | paused | finished
    const [elapsed, setElapsed] = useState(0);    // seconds
    const [distance, setDistance] = useState(0); // meters
    const [path, setPath] = useState([]);         // [{lat,lng,t,acc}]
    const [speed, setSpeed] = useState(0);        // current km/h
    const [maxSpeed, setMaxSpeed] = useState(0);
    const [gpsAvailable, setGpsAvailable] = useState(true);
    const [error, setError] = useState("");

    const watchIdRef = useRef(null);
    const startedAtRef = useRef(null);
    const tickRef = useRef(null);
    const lastPointRef = useRef(null);
    const [history, setHistory] = useState([]);
    const [weekSummary, setWeekSummary] = useState(null);
    const [bodyMetrics, setBodyMetrics] = useState(null);
    const [summaryRun, setSummaryRun] = useState(null);  // shows post-save Strava-style summary modal

    const fetchHistory = useCallback(async () => {
        try {
            const r = await client.get("/runs/me?limit=10");
            setHistory(r.data.runs);
            setWeekSummary(r.data.week);
        } catch (e) {
            // non-fatal
        }
    }, []);

    useEffect(() => {
        fetchHistory();
        client.get("/users/me/body-metrics").then((r) => setBodyMetrics(r.data)).catch(() => {});
    }, [fetchHistory]);

    useEffect(() => {
        if (!navigator.geolocation) setGpsAvailable(false);
    }, []);

    // tick timer
    useEffect(() => {
        if (status === "running") {
            tickRef.current = setInterval(() => {
                if (startedAtRef.current != null) {
                    setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
                }
            }, 250);
            return () => clearInterval(tickRef.current);
        }
    }, [status]);

    const startRun = async () => {
        setError("");
        if (!navigator.geolocation) {
            setError("GPS unavailable on this device — try the mobile browser.");
            return;
        }
        // Reset state
        setElapsed(0);
        setDistance(0);
        setPath([]);
        setSpeed(0);
        setMaxSpeed(0);
        lastPointRef.current = null;
        startedAtRef.current = Date.now();
        setStatus("running");

        try {
            watchIdRef.current = navigator.geolocation.watchPosition(
                (pos) => {
                    const p = {
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude,
                        t: Date.now(),
                        acc: pos.coords.accuracy,
                        alt: pos.coords.altitude || null,
                    };
                    // Filter low-accuracy points
                    if (p.acc && p.acc > 50) return;
                    const last = lastPointRef.current;
                    if (last) {
                        const d = haversine(last, p);
                        // Filter teleports (over 100m between consecutive points likely is a GPS jump)
                        if (d < 200) {
                            const dtSec = (p.t - last.t) / 1000;
                            const kmh = dtSec > 0 ? (d / dtSec) * 3.6 : 0;
                            setDistance((prev) => prev + d);
                            setSpeed(kmh);
                            setMaxSpeed((m) => Math.max(m, kmh));
                        }
                    }
                    lastPointRef.current = p;
                    setPath((prev) => prev.concat([p]));
                },
                (err) => {
                    setError("GPS error: " + err.message);
                    setGpsAvailable(false);
                },
                { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 }
            );
        } catch (e) {
            setError("Could not start GPS: " + e.message);
        }
    };

    const stopRun = () => {
        if (watchIdRef.current != null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
        }
        setStatus("finished");
    };

    const discardRun = () => {
        if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
        setStatus("idle");
        setElapsed(0);
        setDistance(0);
        setPath([]);
        setSpeed(0);
        setMaxSpeed(0);
    };

    const saveRun = async () => {
        try {
            const w = bodyMetrics?.weight_kg || 70;
            const hours = elapsed / 3600;
            const avgKmh = hours > 0 ? distance / 1000 / hours : 0;
            const met = activity === "walk" ? 3.8 : Math.max(6, 1.5 + 1.04 * avgKmh);
            const clientKcal = met * w * hours;
            const payload = {
                activity,
                duration_seconds: Math.round(elapsed),
                distance_meters: Math.round(distance),
                avg_speed_kmh: Number(avgKmh.toFixed(2)),
                max_speed_kmh: Number(maxSpeed.toFixed(2)),
                calories_burned: Number(clientKcal.toFixed(1)),
                gps_path: path.slice(-2000),  // safety cap
                started_at: new Date(startedAtRef.current).toISOString(),
                parley_id: parleyId,
            };
            const r = await client.post("/runs", payload);
            const xp = r.data.xp_awarded || 0;
            if (xp > 0) {
                toast.success(`Run saved · +${xp} XP`);
            } else {
                toast.success("Run saved · daily XP cap reached");
            }
            if (parleyId) {
                navigate(`/app/parleys/${parleyId}`);
            } else {
                // Show Strava-style summary modal with the just-saved run
                setSummaryRun(r.data.run);
                fetchHistory();
            }
        } catch (e) {
            toast.error(formatErr(e));
        }
    };

    const avgKmh = elapsed > 0 ? distance / 1000 / (elapsed / 3600) : 0;
    const pace = distance > 100 ? elapsed / 60 / (distance / 1000) : 0;
    const weight = bodyMetrics?.weight_kg || 70;
    const met = activity === "walk" ? 3.8 : Math.max(6, 1.5 + 1.04 * avgKmh);
    const liveKcal = met * weight * (elapsed / 3600);

    return (
        <div className="px-5 pt-8 pb-8" data-testid="run-tracker-page">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <span className="label-cap text-xs text-neon-orange">Cardio</span>
                    <h1 className="font-display font-black text-4xl uppercase tracking-tight leading-none mt-1">
                        Run Tracker
                    </h1>
                </div>
                {parleyId && (
                    <Link
                        to={`/app/parleys/${parleyId}`}
                        className="text-xs text-neon-green label-cap"
                        data-testid="back-to-parley"
                    >
                        ← Parley
                    </Link>
                )}
            </div>

            {/* Activity Toggle */}
            <div className="mt-4 grid grid-cols-2 gap-2" data-testid="activity-toggle">
                {["run", "walk"].map((a) => (
                    <button
                        key={a}
                        onClick={() => status === "idle" && setActivity(a)}
                        disabled={status !== "idle"}
                        data-testid={`activity-${a}`}
                        className={`py-3 surface-1 font-display uppercase tracking-widest text-sm border ${
                            activity === a
                                ? "border-neon-green text-neon-green shadow-[0_0_12px_rgba(57,255,20,0.4)]"
                                : "text-zinc-400 border-charcoal-500"
                        }`}
                    >
                        {a === "run" ? (
                            <span className="flex items-center justify-center gap-2"><Activity size={14} /> Run</span>
                        ) : (
                            <span className="flex items-center justify-center gap-2"><Footprints size={14} /> Walk</span>
                        )}
                    </button>
                ))}
            </div>

            {/* Live Map (only during a session) */}
            {(status === "running" || status === "finished") && (
                <div className="mt-4" data-testid="run-live-map">
                    <RunMap gpsPath={path} mode={status === "running" ? "live" : "summary"} height={220} />
                </div>
            )}

            {/* Live Display */}
            <motion.div
                className="mt-6 surface-2 p-6 border-l-2 border-l-neon-green"
                animate={status === "running" ? { boxShadow: ["0 0 0px rgba(57,255,20,0.0)", "0 0 24px rgba(57,255,20,0.35)", "0 0 0px rgba(57,255,20,0.0)"] } : {}}
                transition={{ repeat: Infinity, duration: 2.4 }}
            >
                <div className="label-cap text-[10px] text-zinc-500">Distance</div>
                <div className="font-display font-black text-7xl text-neon-green leading-none" data-testid="run-distance">
                    {fmtKm(distance)}<span className="text-2xl text-zinc-400 ml-2">km</span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3">
                    <Stat label="Time" value={fmtTime(elapsed)} icon={Timer} testid="run-time" />
                    <Stat label="Pace" value={pace > 0 ? `${pace.toFixed(1)}` : "—"} unit="/km" icon={Gauge} testid="run-pace" />
                    <Stat label="kcal" value={liveKcal > 0 ? liveKcal.toFixed(0) : "—"} icon={Flame} testid="run-kcal" />
                </div>
                <div className="mt-3 flex items-center justify-between text-[11px] text-zinc-500">
                    <span className="label-cap"><MapPin size={10} className="inline mr-1" />{path.length} GPS pts</span>
                    <span className="label-cap">Avg {avgKmh.toFixed(1)} km/h · Max {maxSpeed.toFixed(1)}</span>
                </div>
            </motion.div>

            {error && (
                <div className="mt-3 surface-1 p-3 border-l-2 border-l-red-500 text-xs text-red-300" data-testid="run-error">
                    {error}
                </div>
            )}

            {!gpsAvailable && status === "idle" && (
                <div className="mt-3 surface-1 p-3 text-xs text-zinc-400">
                    GPS requires permission. On iOS/Android open this page in your mobile browser and grant location access when prompted.
                </div>
            )}

            {/* Controls */}
            <div className="mt-6">
                {status === "idle" && (
                    <button
                        onClick={startRun}
                        data-testid="run-start-button"
                        className="w-full py-4 bg-neon-green text-charcoal-900 font-display font-black uppercase tracking-widest text-lg flex items-center justify-center gap-2 hover:brightness-110"
                        style={{ boxShadow: "0 0 24px rgba(57,255,20,0.5)" }}
                    >
                        <Play size={18} /> Start {activity}
                    </button>
                )}
                {status === "running" && (
                    <button
                        onClick={stopRun}
                        data-testid="run-stop-button"
                        className="w-full py-4 bg-red-500 text-white font-display font-black uppercase tracking-widest text-lg flex items-center justify-center gap-2"
                    >
                        <Square size={18} /> Stop
                    </button>
                )}
                {status === "finished" && (
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            onClick={discardRun}
                            data-testid="run-discard-button"
                            className="py-3 surface-1 text-zinc-400 font-display font-bold uppercase tracking-widest border-charcoal-500 flex items-center justify-center gap-2"
                        >
                            <Trash2 size={14} /> Discard
                        </button>
                        <button
                            onClick={saveRun}
                            data-testid="run-save-button"
                            disabled={distance < 10 && elapsed < 30}
                            className="py-3 bg-neon-orange text-charcoal-900 font-display font-black uppercase tracking-widest disabled:opacity-50"
                        >
                            Save Run
                        </button>
                    </div>
                )}
            </div>

            {/* This Week Summary */}
            {weekSummary && (
                <div className="mt-8" data-testid="run-week-summary">
                    <h2 className="font-display font-black text-lg uppercase tracking-tight mb-3">
                        This Week
                    </h2>
                    <div className="grid grid-cols-3 gap-2">
                        <Stat label="Runs" value={weekSummary.count} testid="week-count" />
                        <Stat label="Distance" value={fmtKm(weekSummary.distance_meters)} unit="km" testid="week-distance" />
                        <Stat label="kcal" value={Math.round(weekSummary.calories_burned)} testid="week-kcal" />
                    </div>
                </div>
            )}

            {/* Recent Runs */}
            {history.length > 0 && (
                <div className="mt-8" data-testid="run-history">
                    <h2 className="font-display font-black text-lg uppercase tracking-tight mb-3">Recent</h2>
                    <div className="space-y-2">
                        {history.map((r) => (
                            <Link
                                key={r.id}
                                to={`/app/runs/${r.id}`}
                                data-testid={`run-history-${r.id}`}
                                className="surface-1 p-3 flex items-center gap-3 hover:border-neon-green transition-colors"
                            >
                                <div className={`w-9 h-9 rounded flex items-center justify-center ${r.activity === "walk" ? "bg-zinc-700" : "bg-neon-green/20"}`}>
                                    {r.activity === "walk" ? <Footprints size={16} /> : <Activity size={16} className="text-neon-green" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-bold uppercase text-sm">
                                        {fmtKm(r.distance_meters)} km · {fmtTime(r.duration_seconds)}
                                    </div>
                                    <div className="text-[11px] text-zinc-500">
                                        {Math.round(r.calories_burned)} kcal · {new Date(r.started_at).toLocaleDateString()}
                                    </div>
                                </div>
                                <ChevronRight size={14} className="text-zinc-600" />
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Post-save Strava-style summary modal */}
            {summaryRun && (
                <RunSummary
                    run={summaryRun}
                    isModal
                    onClose={() => {
                        setSummaryRun(null);
                        discardRun();
                    }}
                />
            )}
        </div>
    );
}

function Stat({ label, value, unit, icon: Icon, testid }) {
    return (
        <div className="surface-1 p-3" data-testid={testid}>
            <div className="label-cap text-[10px] text-zinc-500 flex items-center gap-1">
                {Icon && <Icon size={10} />} {label}
            </div>
            <div className="font-display font-black text-xl mt-1 leading-none">
                {value}
                {unit && <span className="text-[10px] text-zinc-400 ml-1">{unit}</span>}
            </div>
        </div>
    );
}
